import { FC, useEffect, useState } from "react";
import { ProviderInvoice } from "../../../type/entities/provider-invoice-type";
import * as yup from "yup";
import { ProviderInvoiceService } from "../../../services/providerInvoices/providerInvoiceService";
import { useAtom } from "jotai";
import { formsSubmitAtom } from "../../../atom/atom";
import useHandleErrors from "../../../hooks/useHandleErrors";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import useProviders from "../../../hooks/api-calls/useProviders";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { useFormik } from "formik";
import { toast } from "sonner";
import RequiredInput from "../../../components/forms/InputHOC";
import CustomSelect from "../../../components/forms/CustomSelect";
import { Switch, Textarea } from "rizzui";
import { es } from "date-fns/locale";
import moment from "moment";
import { DatePicker } from "../../../components/forms/DatePicker";

interface ProviderInvoiceFormProps {
    setOpenModal: any;
    refetch: () => void;
    providerInvoice?: ProviderInvoice | null;
    formRef?: any;
}


const schema = yup.object({
    providerId: yup.string().required("Proveedor es requerido"),
    invoiceDate: yup.string().required("Fecha de factura es requerida"),
    totalAmount: yup.number().required("Importe es requerido"),
    isPaid: yup.boolean().nullable(),
    paymentDate: yup.string().nullable(),
    ivaTax: yup.number().required("IVA es requerido"),
})

const superAdminSchema = schema.concat(yup.object({
    company: yup.string().required('La compañía es obligatoria'),
}))


const ProvidersInvoicesForm: FC<ProviderInvoiceFormProps> = ({
    setOpenModal,
    refetch,
    providerInvoice,
    formRef,
}) => {

    // STATES

    const service = new ProviderInvoiceService();
    const isEdit = providerInvoice !== null;
    const [isSearched, setIsSearched] = useState(false);

    // HOOKS

    const [,setIsSubmitting] = useAtom(formsSubmitAtom);
    const { handleErrors } = useHandleErrors();
    const { getCompaniesList }  = useCompanies();
    const { getProvidersList } = useProviders();
    const { userCan } = usePrivilege();

    // FORMIK

    const formik = useFormik({
        initialValues: {
            id: providerInvoice?.id ?? '',
            providerId: providerInvoice?.provider?.id ?? '',
            invoiceDate: providerInvoice?.invoiceDate.date ?? '',
            totalAmount: providerInvoice?.totalAmount ?? '',
            isPaid: providerInvoice?.isPaid ?? false,
            paymentDate: providerInvoice?.paymentDate?.date ?? null,
            comments: providerInvoice?.comments ?? '',
            company: providerInvoice?.company?.id ?? '',
            ivaTax: providerInvoice?.ivaTax ?? '',
        },
        validationSchema: userCan('list', 'company') ? superAdminSchema : schema,
        validateOnBlur: false,
        onSubmit: values => {
            if (!isEdit) {
                handleCreate(values)
            } else {
                handleEdit(values)
            }
        },
    });

    // FUNCTIONS 

    //----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR UNA NUEVA FACTURA
     * @EN CREATE A NEW INVOICE
     * 
     * @param values 
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            // Formatear las fechas al formato YYYY-MM-DD antes de enviar al backend
            const formattedValues = {
                ...values,
                invoiceDate: values.invoiceDate ? moment(values.invoiceDate).format('YYYY-MM-DD') : null,
                paymentDate: values.paymentDate ? moment(values.paymentDate).format('YYYY-MM-DD') : null,
            };
            
            const response = await (await service.createProviderInvoice(formattedValues)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Factura creada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    }
    //----------------------------------------------------------------------------------------------------------------------


    //----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UNA FACTURA
     * @EN EDIT AN INVOICE
     * 
     * @param values 
     */
    //----------------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {
            // Formatear las fechas al formato YYYY-MM-DD antes de enviar al backend
            const formattedValues = {
                ...values,
                invoiceDate: values.invoiceDate ? moment(values.invoiceDate).format('YYYY-MM-DD') : null,
                paymentDate: values.paymentDate ? moment(values.paymentDate).format('YYYY-MM-DD') : null,
            };
            
            const response = await (await service.editProviderInvoice(formattedValues)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Factura editada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    }
    //----------------------------------------------------------------------------------------------------------------------

    
    // USE EFFECT

    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting])

    useEffect(() => {
        if (!formik.values.company && getCompaniesList().length > 0 && !isSearched) {
            setIsSearched(true);
            let defaultCompany = getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU');
            formik.setFieldValue('company', defaultCompany?.value ?? '');
        }
    }, [getCompaniesList]);
    
    // RENDER

    return (
        <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef ? formRef : undefined}>
                <div className={'md:col-span-12'}>
                    <div className='md:grid md:grid-cols-12 md:gap-6'>

                        {userCan('list', 'company') && (
                            <CustomSelect
                            required
                            isSearchable
                            id={'company'}
                            label="Compañia"
                            containerClassName='md:col-span-12'
                            value={
                                formik.values.company ?
                                { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU')
                            }
                            options={getCompaniesList()}
                            onChange={
                                (e: any) => { formik.setFieldValue('company', e?.value) }
                            }
                            error={formik.errors.company}
                            />
                        )}

                        <CustomSelect
                            required
                            isSearchable
                            id={'providerId'}
                            label="Proveedor"
                            containerClassName='md:col-span-12'
                            value={{ value: formik.values.providerId, label: getProvidersList()?.find((g: any) => g.value === formik.values.providerId)?.label }}
                            options={getProvidersList()}
                            onChange={
                                (e: any) => { formik.setFieldValue('providerId', e?.value) }
                            }
                            error={formik.errors.providerId}
                        />
                        
                        {!isEdit && (<RequiredInput
                            id='totalAmount'
                            label="Importe (Sin IVA)"
                            className='md:col-span-12'
                            formik={formik}
                            error={formik.errors.totalAmount}
                        />)}

                        <RequiredInput
                            id='ivaTax'
                            label="IVA"
                            className='md:col-span-12'
                            formik={formik}
                            error={formik.errors.ivaTax}   
                        />
                                
                        <div className="md:col-span-12">
                            <label htmlFor="invoiceDate" className="block text-sm font-medium text-gray-700 mb-2">
                                Fecha de la factura <span className="text-red-500">*</span>
                            </label>
                            <DatePicker
                                monthsShown={1}
                                placeholderText="Selecciona una fecha"
                                inputProps={{
                                    inputClassName: `h-9 [&_input]:text-ellipsis bg-white ${formik.errors.invoiceDate ? 'border-red-500' : ''}`,
                                }}
                                className="col-span-12"
                                //@ts-ignore
                                selected={formik.values?.invoiceDate ? moment(formik.values.invoiceDate).toDate() : null}
                                onChange={(date: any) => {
                                    formik.setFieldValue('invoiceDate', date);
                                }}
                                locale={es}
                            />
                            {formik.errors.invoiceDate && (
                                <p className="text-red-500 text-sm">{formik.errors.invoiceDate?.toString()}</p>
                            )}
                        </div>


                        <div className="md:col-span-12">
                            <label htmlFor="paymentDate" className="block text-sm font-medium text-gray-700 mb-2">
                                Fecha de pago <span className="text-red-500">*</span>
                            </label>
                            <DatePicker
                                monthsShown={1}
                                isClearable
                                placeholderText="Selecciona una fecha"
                                inputProps={{
                                    inputClassName: `h-9 [&_input]:text-ellipsis bg-white ${formik.errors.paymentDate ? 'border-red-500' : ''}`,
                                }}
                                className="col-span-12"
                                //@ts-ignore
                                selected={formik.values?.paymentDate ? moment(formik.values.paymentDate).toDate() : null}
                                onChange={(date: any) => {
                                    formik.setFieldValue('paymentDate', date);
                                    if(date !== null) {
                                        formik.setFieldValue('isPaid', true);
                                    } else {
                                        formik.setFieldValue('isPaid', false);
                                    }
                                }}
                                locale={es}
                            />
                            {formik.errors.paymentDate && (
                                <p className="text-red-500 text-sm">{formik.errors.paymentDate?.toString()}</p>
                            )}
                        </div>

                        <Textarea
                            id='comments'
                            label='Comentarios'
                            rows={4}
                            value={formik.values.comments}
                            onChange={(e) => formik.setFieldValue('comments', e.target.value)}
                            className="mb-4 md:col-span-12"
                        />

                        <Switch
                            id='isPaid'
                            label='Pagada'
                            checked={formik.values.isPaid}
                            onChange={(e) => formik.setFieldValue('isPaid', e.target.checked)}
                            className="mb-4 md:col-span-12"
                        />
                    </div>
                </div>
            </form>
        </div>
    )
}

export default ProvidersInvoicesForm;